<script setup lang="ts">
import { capitalize, get } from 'lodash'
import { computed, ref, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useExtendedI18n } from '@/i18n'
import {
  ActionItem,
  ActionsGroup,
  ActionsMenu,
  VBadge,
  VButton,
  VDropdownImproved,
  VTable,
  VModal,
  VSection,
  VTextField,
  VTextArea,
} from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'
import { useActionsMenu } from '@/modules/shared/utils/actions-menu'
import { rails_url } from '@/modules/shared/utils/rails'
import { createQueryString } from '@/modules/shared/utils/routing'
import { useInvoiceStore } from '../stores/invoice-store'
import { investorPath } from '../utils/investor'
import { format } from '@/modules/shared/utils/v-table'
import InvoicePaymentFormModal from './InvoicePaymentFormModal.vue'
import InvoiceWrapper from './InvoiceWrapper.vue'

const { n, t } = useExtendedI18n()
const route = useRoute()
const props = withDefaults(
  defineProps<{
    invoices: any[]
    investable_id: string
    investable_type: string
    skeleton: boolean
    enable_multiple_payment?: boolean
    enable_links?: boolean
    hidden_columns?: string[]
    isAdmin?: boolean
  }>(),
  {
    enable_links: true,
  },
)
const invoiceStore = useInvoiceStore()
const selectedInvoiceType = ref(route.query?.filter || 'all')
const selectedInvoices = ref<number[]>([])
const selectedInvestorKeys = ref<string[]>([])
const invoicePaymentFormModal = ref()
const loading = ref(false)
let hasInvoiceAlreadyOpened = false // To avoid opening the invoice modal when we select tabs, selecting tabs renders the whole table again so the modal would open again

const isInvoiceOpenedByDefault = (invoice_id) => {
  if (hasInvoiceAlreadyOpened) return null
  return route.query.invoice_id?.toString() === invoice_id.toString()
}
const pdfLoading = ref(false)

const updateURLQuery = () => {
  const query = {}
  if (selectedInvoiceType.value !== 'all') {
    query.tab = selectedInvoiceType.value
  }
  if (selectedInvestorKeys.value.length !== investorOptions.value.length) {
    query.investor = selectedInvestorKeys.value.join(',')
  }
  const queryString = createQueryString(query)
  window.history.replaceState(null, null, `?${queryString}`)
}

const investorOptions = computed(() =>
  Object.values(
    props.invoices.reduce((acc, invoice) => {
      acc[invoice.investor._cid] = invoice.investor
      return acc
    }, {}),
  ).map((investor) => ({ label: investor.name, value: investor._cid })),
)
const viewingAs = computed(() => {
  if (selectedInvestorKeys.value.length === investorOptions.value.length) {
    return `all ${t('shared.profile', 0)}`
  }

  if (selectedInvestorKeys.value.length === 1) {
    return investorOptions.value.find((option) => option.value === selectedInvestorKeys.value[0]).label
  }

  if (selectedInvestorKeys.value.length > 1 || selectedInvestorKeys.value.length < 1) {
    return `${selectedInvestorKeys.value.length} ${t('shared.investor', selectedInvestorKeys.value.length)}`
  }
})

const hidden_columns = computed(() => props.hidden_columns || [])
const toggleAllInvoice = (e) => {
  if (e.target.checked) {
    selectedInvoices.value = current_invoices.value.map((invoice) => invoice.id)
  } else {
    selectedInvoices.value = []
  }
}
const toggleInvoiceSelection = (id: number) => {
  mark_single_invoice.value = false
  if (selectedInvoices.value.includes(id)) {
    selectedInvoices.value = selectedInvoices.value.filter((invoiceId) => invoiceId !== id)
  } else {
    selectedInvoices.value.push(id)
  }
}
const selectInvoiceType = (type: string) => {
  selectedInvoiceType.value = type
  updateURLQuery()
}

const current_invoices = computed(() =>
  props.invoices
    .filter((invoice) => {
      if (selectedInvoiceType.value === 'all') {
        return true
      }

      if (selectedInvoiceType.value === 'outstanding') {
        return !invoice.marked_as_paid
      }

      if (selectedInvoiceType.value === 'paid') {
        return invoice.marked_as_paid
      }
    })
    .filter((invoice) => {
      if (invoice.investor._cid.startsWith('funding-entity')) {
        return (
          selectedInvestorKeys.value.includes(invoice.investor._cid) ||
          selectedInvestorKeys.value.includes(invoice.investor._cid.replace('funding-entity', 'associated-entity'))
        )
      } else {
        return selectedInvestorKeys.value.includes(invoice.investor._cid)
      }
    }),
)
const outstanding_invoice_ids = computed(() =>
  props.invoices.filter((invoice) => !invoice.marked_as_paid).map((invoice) => invoice.id),
)
const paid_invoice_ids = computed(() =>
  props.invoices.filter((invoice) => invoice.marked_as_paid).map((invoice) => invoice.id),
)

const initialState = {
  invoice_ids: [],
  paid_at: null,
  instructions: null,
}
const rules = {
  invoice_ids: { required },
  paid_at: { required },
}
const invoicePaymentForm = ref({ ...initialState })
const v$ = useVuelidate(rules, invoicePaymentForm, { $lazy: true })
const actionsMenu = useActionsMenu(['send_reminder', 'mark_as_paid', 'mark_as_unpaid', 'delete'])
const mark_single_invoice = ref(false)

function resetSelectedInvoiceIds() {
  selectedInvoices.value = []
}

const openInvoicePaymentForm = () => {
  mark_single_invoice.value = false
  const invoice_ids = selectedInvoices.value.filter((invoice_id) => outstanding_invoice_ids.value.includes(invoice_id))
  if (invoice_ids.length === 0) return alert('Please select at least one outstanding invoice to mark as paid')
  invoicePaymentFormModal.value?.modal?.open()
}

const invoiceCallback = async () => {
  loading.value = true
  await invoiceStore.fetchInvoices(props.investable_type, props.investable_id)
  loading.value = false
  selectedInvoices.value = []
}

const markInvoicesAsUnpaid = async () => {
  const invoice_ids = selectedInvoices.value.filter((invoice_id) => paid_invoice_ids.value.includes(invoice_id))
  if (invoice_ids.length === 0) return alert('Please select at least one paid invoice to mark as unpaid')

  const confirm = window.confirm('Are you sure you want to mark the selected invoices as unpaid?')
  if (!confirm) return

  loading.value = true
  await invoiceStore.markMultipleInvoicesAsUnpaid(invoice_ids)
  await invoiceStore.fetchInvoices(props.investable_type, props.investable_id)
  loading.value = false

  resetSelectedInvoiceIds()
}

const sendReminder = async (invoice) => {
  actionsMenu.setLoading('send_reminder', true)
  await invoiceStore.sendReminder(invoice.id)
  actionsMenu.setLoading('send_reminder', false)
}

const markInvoiceAsUnpaid = async (invoice) => {
  const confirm = window.confirm('Are you sure you want to mark the invoice as unpaid?')
  if (!confirm) return

  actionsMenu.setLoading('mark_as_unpaid', true)
  await invoiceStore.markInvoiceAsUnpaid(invoice.id)
  await invoiceStore.fetchInvoices(props.investable_type, props.investable_id)
  actionsMenu.setLoading('mark_as_unpaid', false)
}

const markInvoiceAsPaid = (invoice) => {
  mark_single_invoice.value = true
  selectedInvoices.value = [invoice.id]
  invoicePaymentFormModal.value?.modal?.open()
}

const removeInvoice = async (invoice) => {
  const confirm = window.confirm('Are you sure you want to delete the invoice?')
  if (!confirm) return

  actionsMenu.setLoading('delete', true)
  await invoiceStore.removeInvoice(invoice.id)
  await invoiceStore.fetchInvoices(props.investable_type, props.investable_id)
  actionsMenu.setLoading('delete', false)
}

watch(
  () => props.skeleton,
  (v) => {
    if (route.query.tab) {
      selectedInvoiceType.value = route.query.tab
    }

    if (route.query.investor || route.query.investor === '') {
      selectedInvestorKeys.value = route.query.investor.split(',')
    } else {
      selectedInvestorKeys.value = Object.values(
        props.invoices.reduce((acc, invoice) => {
          acc[invoice.investor._cid] = invoice.investor._cid
          return acc
        }, {}),
      )
    }
  },
)
const onOpenInvoiceDetails = (invoice_id) => {
  hasInvoiceAlreadyOpened = true
}

const downloadPdf = async (invoice) => {
  if (pdfLoading.value) return
  pdfLoading.value = true
  try {
    await invoiceStore.downloadInvoicePdf(invoice.id)
  } finally {
    pdfLoading.value = false
  }
}
</script>

<template>
  <div class="relative z-50 mb-3 flex items-center justify-between">
    <div class="flex items-center space-x-1.5">
      <VButton :active="selectedInvoiceType === 'all'" class="w-32" @click="selectInvoiceType('all')">
        <span>{{ capitalize(t('shared.all')) }}</span>
      </VButton>
      <VButton :active="selectedInvoiceType === 'outstanding'" class="w-32" @click="selectInvoiceType('outstanding')">
        <span>{{ capitalize(t('shared.outstanding')) }}</span>
      </VButton>
      <VButton :active="selectedInvoiceType === 'paid'" class="w-32" @click="selectInvoiceType('paid')">
        <span>{{ capitalize(t('shared.paid')) }}</span>
      </VButton>
      <VDropdownImproved
        v-model="selectedInvestorKeys"
        :onChange="updateURLQuery"
        aligned="left"
        :options="investorOptions"
        class="z-[1]"
      >
        <VButton size="md">
          <div class="flex items-center space-x-2">
            <div>Filter by {{ viewingAs }}</div>
            <VIcon name="ChevronSelectorVertical" />
          </div>
        </VButton>
      </VDropdownImproved>
    </div>
    <div class="flex items-center space-x-1.5" v-if="props.enable_multiple_payment">
      <VButton size="sm" variant="v-blue" :loading="loading" :click="markInvoicesAsUnpaid">
        {{ capitalize(t('shared.mark as unpaid')) }}
      </VButton>
      <VButton size="sm" variant="v-blue" :disabled="loading" :click="openInvoicePaymentForm">
        {{ capitalize(t('shared.mark as paid')) }}
      </VButton>
    </div>
  </div>
  <VTable
    :columns="[
      {
        key: 'checkbox',
        name: '',
        type: 'string',
        is_visible: true,
        is_accessible: props.enable_multiple_payment,
        enable_thead_slot: true,
      },
      {
        key: 'name',
        name: capitalize(t('shared.name')),
        type: 'string',
        fixed: true,
        is_visible: !hidden_columns.includes('name'),
      },
      {
        key: 'investor.name',
        name: capitalize(t('shared.investor')),
        type: 'string',
        is_visible: !hidden_columns.includes('investor'),
      },
      {
        key: 'entity.name',
        name: 'Entity',
        type: 'string',
        is_visible: !hidden_columns.includes('entity.name'),
      },
      {
        key: 'date',
        name: capitalize(t('shared.due date')),
        type: 'date',
        sorted: true,
        is_visible: !hidden_columns.includes('date'),
      },
      {
        key: 'marked_as_paid',
        name: capitalize(t('shared.status')),
        type: 'boolean',
        is_visible: !hidden_columns.includes('status'),
      },
      {
        key: 'paid_at',
        name: capitalize(t('shared.paid date')),
        type: 'date',
        align: 'left',
        is_visible: !hidden_columns.includes('paid_at'),
      },
      {
        key: 'marked_as_paid_by_user.name',
        name: capitalize(t('shared.marked as paid by')),
        type: 'string',
        align: 'left',
        is_visible: !hidden_columns.includes('marked_as_paid_by_user'),
      },
      {
        key: 'total_amount',
        name: capitalize(t('shared.total amount due')),
        type: 'currency',
        is_visible: !hidden_columns.includes('total_amount'),
        aggregate: 'sum',
      },
      {
        key: 'actions',
        name: '',
        type: 'actions',
        is_visible: isAdmin,
      },
    ]"
    :items="current_invoices"
    :name="`${investable_type}-${investable_id}-invoices`"
    :skeleton="skeleton"
    :slots="['actions', 'investor.name', 'name', 'marked_as_paid', 'checkbox']"
  >
    <template #thead.checkbox>
      <input
        type="checkbox"
        class="cursor-pointer"
        :checked="current_invoices.length > 0 && selectedInvoices.length === current_invoices.length"
        @change="toggleAllInvoice"
      />
    </template>
    <template #checkbox="{ item }">
      <input
        type="checkbox"
        :checked="selectedInvoices.includes(item.id) && !mark_single_invoice"
        @change="toggleInvoiceSelection(item.id)"
      />
    </template>
    <template #investor.name="{ item }">
      <RouterLink
        v-if="enable_links"
        class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
        :to="investorPath(get(item, 'investor._cid'))"
      >
        {{ get(item, 'investor.name') }}
      </RouterLink>
      <span v-else>
        {{ get(item, 'investor.name') }}
      </span>
    </template>
    <template #name="{ item }">
      <InvoiceWrapper
        :invoiceId="item.id"
        :status="item.display_status"
        :callback="invoiceCallback"
        :onOpenDetails="() => onOpenInvoiceDetails(item.id)"
        :isOpenByDefault="isInvoiceOpenedByDefault(item.id)"
        :enable_links="enable_links"
      >
        <template #label>
          <div class="hyperlink">
            {{ get(item, 'name') }}
          </div>
        </template>
      </InvoiceWrapper>
    </template>
    <template #marked_as_paid="{ item }">
      {{ item.marked_as_paid ? 'paid' : 'outstanding' }}
    </template>
    <template #entity.name="{ item }">
      <div v-if="item.is_call_invoice && ['fund', 'spv'].includes(get(item, 'entity.entity_type')?.toLowerCase())">
        {{ get(item, 'entity.name') }}
      </div>
    </template>
    <template #actions="{ item }">
      <div class="flex items-center justify-end space-x-1.5">
        <ActionsMenu v-if="isAdmin">
          <ActionsGroup>
            <ActionItem
              v-if="!item.marked_as_paid"
              :text="capitalize(t('shared.send reminder'))"
              @click="() => sendReminder(item)"
              :loading="actionsMenu.actions.value.send_reminder?.loading"
            />
            <ActionItem
              v-if="!item.marked_as_paid"
              :text="capitalize(t('shared.mark as paid'))"
              @click="() => markInvoiceAsPaid(item)"
            />
            <ActionItem
              v-else
              :text="capitalize(t('shared.mark as unpaid'))"
              @click="() => markInvoiceAsUnpaid(item)"
              :loading="actionsMenu.actions.value.mark_as_unpaid?.loading"
            />
            <ActionItem
              :text="capitalize(t('shared.delete'))"
              @click="() => removeInvoice(item)"
              :loading="actionsMenu.actions.value.delete?.loading"
            />
          </ActionsGroup>
        </ActionsMenu>
      </div>
    </template>
  </VTable>
  <InvoicePaymentFormModal ref="invoicePaymentFormModal" :invoiceIds="selectedInvoices" :callback="invoiceCallback" />
</template>

<style scoped>
table td {
  @apply whitespace-nowrap pb-2 pr-5  first:w-[1%];
}
table td:nth-child(1) {
  @apply text-left;
}
table td:nth-child(2) {
  @apply text-right;
}
</style>
